/**
 * The five-second "these are the parts an agent can drive" label.
 *
 * Shown once per surface open. It lists what is REALLY registered, read from
 * the activity store, so it cannot drift from the tools the way a hand-written
 * list would. If nothing is registered it renders nothing rather than
 * advertising a capability the build does not have.
 *
 * It also switches the surface into `data-agent-hint` for its lifetime, which
 * is what draws the outline on each touchable component — the label says "these
 * are the components" and the components agree, instead of the reader guessing
 * which ones were meant.
 */
import { useEffect, useMemo, useState } from 'react';
import { Sparkles, X } from 'lucide-react';

import { hasAgentSurface, useAgentActivity } from '../agentActivity';
import { cn } from '@/lib/utils';

const VISIBLE_MS = 5000;
/** Long enough to read, short enough that the outline is not decoration. */
const FADE_MS = 400;

interface AgentSurfaceIntroProps {
  /** Re-arms the label. Change it when the surface is opened again. */
  readonly surfaceKey: string;
  readonly className?: string;
}

export function AgentSurfaceIntro({ surfaceKey, className }: AgentSurfaceIntroProps) {
  const touchables = useAgentActivity((s) => s.touchables);
  const [dismissed, setDismissed] = useState(false);
  const [leaving, setLeaving] = useState(false);

  const items = useMemo(
    () => (hasAgentSurface() ? Object.values(touchables) : []),
    [touchables],
  );

  // Re-arm on every open. Keyed on the surface so switching tabs and coming
  // back shows it again, which is the point: it teaches the surface, not the
  // session.
  useEffect(() => {
    setDismissed(false);
    setLeaving(false);
  }, [surfaceKey]);

  useEffect(() => {
    if (dismissed || items.length === 0) return;
    const fade = window.setTimeout(() => setLeaving(true), VISIBLE_MS - FADE_MS);
    const hide = window.setTimeout(() => setDismissed(true), VISIBLE_MS);
    return () => {
      window.clearTimeout(fade);
      window.clearTimeout(hide);
    };
  }, [dismissed, items.length, surfaceKey]);

  // While the label is up, the surface marks its agent-touchable components.
  useEffect(() => {
    const root = document.documentElement;
    const show = !dismissed && items.length > 0;
    if (show) root.setAttribute('data-agent-hint', 'on');
    else root.removeAttribute('data-agent-hint');
    return () => root.removeAttribute('data-agent-hint');
  }, [dismissed, items.length]);

  if (dismissed || items.length === 0) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'flex items-start gap-3 rounded-lg border border-teal/30 bg-teal-light/60 px-3 py-2',
        'transition-opacity duration-300',
        leaving ? 'opacity-0' : 'opacity-100',
        className,
      )}
    >
      <Sparkles className="mt-0.5 size-4 shrink-0 text-teal-dark" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-medium leading-snug text-teal-dark">
          These are the components WebMCP can modify — try interacting with them.
        </p>
        <p className="mt-0.5 truncate text-[12px] leading-snug text-ink-2">
          {items.map((item) => `${item.label} · ${item.can}`).join('   |   ')}
        </p>
      </div>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss agent hint"
        className="shrink-0 rounded p-1 text-ink-3 transition-colors hover:bg-teal/10 hover:text-teal-dark"
      >
        <X className="size-3.5" />
      </button>
    </div>
  );
}
